"use client";

import * as React from "react";
import { usePathname } from "next/navigation";
import { ThemeProvider as NextThemesProvider } from "next-themes";

import {
  ThemeAccessContext,
  THEME_STORAGE_KEY,
  useAppTheme
} from "@/hooks/useAppTheme";
import { isBareKeyShortcut } from "@/lib/bare-key-shortcut";
import { shortcutKey } from "@/lib/keyboard-shortcuts";
import { useHotkey } from "@/components/ui/use-hotkey";
import { isThemedPath } from "./theme-paths";

/** Renders the page in one theme whatever the viewer's preference, e.g. a gallery story in dark. */
export function ThemeForce({ theme }: { theme: "light" | "dark" }) {
  const { setForced } = React.useContext(ThemeAccessContext);
  React.useLayoutEffect(() => {
    setForced(theme);
    return () => setForced(null);
  }, [theme, setForced]);
  return null;
}

/** `D` toggles light and dark, the shadcn convention (BRE-8323). */
function ThemeShortcut() {
  const { enabled, isDark, setPreference } = useAppTheme();
  useHotkey(
    (event) => isBareKeyShortcut(event, shortcutKey("toggle-theme")),
    () => setPreference(isDark ? "light" : "dark"),
    { enabled }
  );
  return null;
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const enabled = isThemedPath(usePathname());
  const [forced, setForced] = React.useState<"light" | "dark" | null>(null);
  const access = React.useMemo(() => ({ enabled, setForced }), [enabled]);

  return (
    <ThemeAccessContext.Provider value={access}>
      <NextThemesProvider
        attribute="class"
        storageKey={THEME_STORAGE_KEY}
        defaultTheme="system"
        forcedTheme={forced ?? (enabled ? undefined : "light")}
        enableSystem
        themes={["light", "dark"]}
        disableTransitionOnChange
      >
        <ThemeShortcut />
        {children}
      </NextThemesProvider>
    </ThemeAccessContext.Provider>
  );
}
